import axios, { AxiosResponse } from "axios";
import { API_CONSTS } from "../constants/api.consts";
import { ApiError, ApiErrorResponse, ApiResponse } from "../types/api";

// API Base Configuration
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8080/api";

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for adding auth tokens if needed
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem("authToken");

      const failedRequest = {
        url: error.config.url,
        method: error.config.method,
        data: error.config.data,
      };

      const queue = JSON.parse(
        localStorage.getItem(API_CONSTS.RETRY_QUEUE) || "[]"
      );
      queue.push(failedRequest);

      // Optionally redirect to login
      window.location.href = `${import.meta.env.VITE_MAIN_APP_BASE_URL}/student-login?redirect=${window.location.href}`;
    }
    return Promise.reject(error);
  }
);

// Generic API wrapper with error handling
export const apiRequest = async <T>(
  request: () => Promise<AxiosResponse<ApiResponse<T>>>
): Promise<T> => {
  try {
    const response = await request();
    if (!response.data.success) {
      throw new Error(response.data.message || "API request failed");
    }
    return response.data.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      // Check for 405 error or other status codes with ApiErrorResponse structure
      const errorResponse = error.response?.data as ApiErrorResponse | ApiError;

      // Handle ApiErrorResponse structure (405 and similar errors)
      if (
        errorResponse &&
        "message" in errorResponse &&
        typeof errorResponse.message === "string"
      ) {
        throw new Error(errorResponse.message);
      }

      // Handle ApiError structure (other errors)
      const apiError = errorResponse as ApiError;
      throw new Error(
        apiError?.error?.message || error.message || "Network error occurred"
      );
    }
    throw error;
  }
};
